import { appState } from '../../store/appState.js';
import { fetchTransactions } from '../../api/transactions.js';
import Chart from 'chart.js/auto';

let filterMode = 'month'; // 'month' | 'year'
let currentDate = new Date();

let cumulativeChartInstance = null;
let donutChartInstance = null;
let barChartInstance = null;

let currentDonutType = 'expense'; // 'expense' | 'income'

let drilldownHistory = []; // Stack to support back button: [{type, title, subtitle, data, total}]

export function initStatsModals() {
    const drilldownModal = document.getElementById('stats-drilldown-modal');
    const drilldownContent = document.getElementById('stats-drilldown-content');
    const bgClose = document.getElementById('btn-close-stats-drilldown');
    const btnBack = document.getElementById('btn-stats-drilldown-back');

    if (!drilldownModal) return;

    bgClose.addEventListener('click', closeDrilldownModal);
    
    drilldownModal.addEventListener('click', (e) => {
        if (e.target === drilldownModal) {
            closeDrilldownModal();
        }
    });
    
    btnBack.addEventListener('click', () => {
        drilldownHistory.pop(); // Remove current
        if (drilldownHistory.length > 0) {
            const previousState = drilldownHistory[drilldownHistory.length - 1];
            renderDrilldownList(previousState, false); // false to not push again
        } else {
            closeDrilldownModal();
        }
    });
}

function closeDrilldownModal() {
    const drilldownModal = document.getElementById('stats-drilldown-modal');
    const drilldownContent = document.getElementById('stats-drilldown-content');
    drilldownContent.classList.remove('translate-y-0');
    drilldownContent.classList.add('translate-y-full');
    setTimeout(() => {
        drilldownModal.classList.add('hidden');
        drilldownModal.classList.remove('flex');
        drilldownHistory = [];
    }, 300);
}

export function openDrilldownModal(state) {
    if (drilldownHistory.length === 0 || drilldownHistory[drilldownHistory.length - 1].type !== state.type || drilldownHistory[drilldownHistory.length - 1].title !== state.title) {
        renderDrilldownList(state, true);
    }
}

function renderDrilldownList(state, pushToHistory = true) {
    if (pushToHistory) {
        drilldownHistory.push(state);
    }

    const { type, title, subtitle, data, total, isIncome } = state;

    document.getElementById('stats-drilldown-title').innerText = title;
    document.getElementById('stats-drilldown-subtitle').innerText = subtitle;
    const colorClass = isIncome ? 'text-income' : 'text-expense';
    document.getElementById('stats-drilldown-total').innerHTML = `<span class="${colorClass}">${isIncome ? '+' : '-'}${total.toLocaleString('en-US')}</span> <span class="text-sm">VND</span>`;

    const btnBack = document.getElementById('btn-stats-drilldown-back');
    if (drilldownHistory.length > 1) {
        btnBack.classList.remove('hidden');
    } else {
        btnBack.classList.add('hidden');
    }

    const listContainer = document.getElementById('stats-drilldown-list');
    listContainer.innerHTML = '';

    // data: [{name, amount, percent, nextStateGetter (optional)}]
    data.forEach(item => {
        const div = document.createElement('div');
        div.className = 'bg-darkbg p-3 rounded-xl border border-gray-800 flex justify-between items-center transition';
        if (item.nextStateGetter) {
            div.classList.add('cursor-pointer', 'active:bg-gray-800', 'hover:bg-gray-800');
            div.onclick = () => {
                const ns = item.nextStateGetter();
                if (ns && ns.data && ns.data.length > 0) openDrilldownModal(ns);
            };
        }
        
        const itemIsIncome = item.hasOwnProperty('isIncome') ? item.isIncome : isIncome;
        
        div.innerHTML = `
            <div class="flex-1">
                <div class="text-white font-bold text-sm">${item.name}</div>
                <div class="w-full bg-gray-700 h-1.5 rounded-full mt-1.5 overflow-hidden">
                    <div class="h-full ${itemIsIncome ? 'bg-income' : 'bg-expense'}" style="width: ${item.percent}%"></div>
                </div>
            </div>
            <div class="ml-4 text-right">
                <div class="text-sm font-bold text-gray-200">${item.amount.toLocaleString('en-US')}</div>
                <div class="text-[10px] text-gray-500">${item.percent}%</div>
            </div>
            ${item.nextStateGetter ? '<i class="fa-solid fa-chevron-right text-gray-600 text-xs ml-3"></i>' : ''}
        `;
        listContainer.appendChild(div);
    });

    const modal = document.getElementById('stats-drilldown-modal');
    const content = document.getElementById('stats-drilldown-content');
    modal.classList.remove('hidden');
    modal.classList.add('flex');
    setTimeout(() => {
        content.classList.remove('translate-y-full');
        content.classList.add('translate-y-0');
    }, 10);
}

export async function renderStatsTab() {
    setupStatsUI();
    const txns = await ensureTransactionsLoaded();
    updateUIHeaders();
    drawCharts(txns);
}

function setupStatsUI() {
    // Prevent multiple bindings
    if (window._statsUIInitialzed) return;
    window._statsUIInitialzed = true;

    initStatsModals();

    const btnMonth = document.getElementById('filter-type-month');
    const btnYear = document.getElementById('filter-type-year');
    
    btnMonth.addEventListener('click', () => {
        if(filterMode === 'month') return;
        filterMode = 'month';
        btnMonth.className = 'px-3 py-1 text-xs font-bold rounded-md bg-gray-600 text-white shadow-sm transition';
        btnYear.className = 'px-3 py-1 text-xs font-bold rounded-md text-gray-400 hover:text-white transition';
        renderStatsTab();
    });

    btnYear.addEventListener('click', () => {
        if(filterMode === 'year') return;
        filterMode = 'year';
        btnYear.className = 'px-3 py-1 text-xs font-bold rounded-md bg-gray-600 text-white shadow-sm transition';
        btnMonth.className = 'px-3 py-1 text-xs font-bold rounded-md text-gray-400 hover:text-white transition';
        renderStatsTab();
    });

    document.getElementById('stats-prev-btn').addEventListener('click', () => {
        if (filterMode === 'month') {
            currentDate.setMonth(currentDate.getMonth() - 1);
        } else {
            currentDate.setFullYear(currentDate.getFullYear() - 1);
        }
        renderStatsTab();
    });

    document.getElementById('stats-next-btn').addEventListener('click', () => {
        if (filterMode === 'month') {
            currentDate.setMonth(currentDate.getMonth() + 1);
        } else {
            currentDate.setFullYear(currentDate.getFullYear() + 1);
        }
        renderStatsTab();
    });

    const donutBtns = document.querySelectorAll('.donut-toggle-btn');
    donutBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
            const type = e.target.getAttribute('data-type');
            if (currentDonutType === type) return;
            currentDonutType = type;
            
            donutBtns.forEach(b => {
                const bType = b.getAttribute('data-type');
                if (bType === type) {
                    b.className = `px-2 py-1 text-[10px] font-bold rounded-md ${type === 'income' ? 'bg-income' : 'bg-expense'} text-white transition donut-toggle-btn`;
                } else {
                    b.className = 'px-2 py-1 text-[10px] font-bold rounded-md text-gray-400 transition donut-toggle-btn';
                }
            });
            renderStatsTab();
        });
    });
}

function updateUIHeaders() {
    const lbl = document.getElementById('stats-date-label');
    if (filterMode === 'month') {
        lbl.innerText = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else {
        lbl.innerText = currentDate.getFullYear().toString();
    }
}

async function ensureTransactionsLoaded() {
    if (!appState.currentTransactionsList || appState.currentTransactionsList.length === 0) {
        appState.currentTransactionsList = await fetchTransactions();
    }
    return appState.currentTransactionsList;
}

Chart.defaults.color = '#9CA3AF';
Chart.defaults.font.family = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial';

function drawCharts(txns) {
    const validTxns = txns.filter(t => !t.linked_txn_id); // Bỏ qua Transfer
    
    const targetMonth = currentDate.getMonth();
    const targetYear = currentDate.getFullYear();

    // 1. DỮ LIỆU CUMULATIVE LINE CHART (Luôn mượt từ quá khứ đến hiện tại)
    // Map data by month-year
    const monthlyNet = {};
    validTxns.forEach(t => {
        const d = new Date(t.transaction_date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyNet[key]) monthlyNet[key] = 0;
        
        const amount = Number(t.base_amount || t.amount);
        if (t.type === 'income') monthlyNet[key] += amount;
        if (t.type === 'expense') monthlyNet[key] -= amount;
    });

    const sortedMonths = Object.keys(monthlyNet).sort();
    const cumulativeLabels = [];
    const cumulativeData = [];
    let runningTotal = 0;
    
    // Nếu App chưa có dữ liệu thì mặc định vài data rỗng
    if (sortedMonths.length === 0) {
        cumulativeLabels.push('No data');
        cumulativeData.push(0);
    } else {
        sortedMonths.forEach(m => {
            runningTotal += monthlyNet[m];
            cumulativeLabels.push(m);
            cumulativeData.push(runningTotal);
        });
    }

    if (cumulativeChartInstance) cumulativeChartInstance.destroy();
    cumulativeChartInstance = new Chart(document.getElementById('chart-cumulative'), {
        type: 'line',
        data: {
            labels: cumulativeLabels,
            datasets: [{
                label: 'Cumulative Net Worth (VND)',
                data: cumulativeData,
                borderColor: '#3B82F6',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 2,
                pointRadius: 0,
                pointHoverRadius: 4,
                fill: true,
                tension: 0.4
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            return ' ' + context.parsed.y.toLocaleString('en-US') + ' VND';
                        }
                    }
                }
            },
            scales: {
                x: { grid: { display: false } },
                y: { grid: { color: '#374151' }, ticks: { callback: function(val) { return (val/1000000).toFixed(0) + 'M'; }} }
            }
        }
    });

    // --- LỌC DỮ LIỆU THEO THỜI GIAN ĐƯỢC CHỌN (Cho Bar và Donut) ---
    const currentPeriodTxns = validTxns.filter(t => {
        const d = new Date(t.transaction_date);
        if (filterMode === 'month') {
            return d.getFullYear() === targetYear && d.getMonth() === targetMonth;
        } else {
            return d.getFullYear() === targetYear;
        }
    });

    // 2. DONUT CHART (Category Structure)
    const categoryTotals = {};
    let totalValueDonut = 0;
    
    const typeFilteredTxns = currentPeriodTxns.filter(t => t.type === currentDonutType);
    
    typeFilteredTxns.forEach(t => {
        const catName = (t.categories && t.categories.name) ? t.categories.name : (t.category_name || 'Unknown');
        const amt = Number(t.base_amount || t.amount);
        
        if (!categoryTotals[catName]) {
            categoryTotals[catName] = { 
                amount: 0, 
                tagTotals: {},
                color: generateColorFromString(catName)
            };
        }
        categoryTotals[catName].amount += amt;
        totalValueDonut += amt;
        
        const tagName = (t.tags && t.tags.name) ? t.tags.name : (t.tag_name || 'No Tag');
        if (!categoryTotals[catName].tagTotals[tagName]) categoryTotals[catName].tagTotals[tagName] = 0;
        categoryTotals[catName].tagTotals[tagName] += amt;
    });

    const donutLabels = [];
    const donutData = [];
    const donutBg = [];
    const categoryObjList = [];

    // Danh sách để nhét vào popup
    Object.keys(categoryTotals).sort((a,b) => categoryTotals[b].amount - categoryTotals[a].amount).forEach(cName => {
        const amt = categoryTotals[cName].amount;
        const color = categoryTotals[cName].color;
        donutLabels.push(cName);
        donutData.push(amt);
        donutBg.push(color);
        
        categoryObjList.push({
            name: cName,
            amount: amt,
            percent: totalValueDonut > 0 ? ((amt / totalValueDonut) * 100).toFixed(1) : 0,
            nextStateGetter: () => {
                // Tạo data tag phụ thuộc category
                const tagList = [];
                const tTotals = categoryTotals[cName].tagTotals;
                Object.keys(tTotals).sort((a,b) => tTotals[b] - tTotals[a]).forEach(tName => {
                    const tAmt = tTotals[tName];
                    tagList.push({
                        name: tName,
                        amount: tAmt,
                        percent: amt > 0 ? ((tAmt / amt) * 100).toFixed(1) : 0
                    });
                });
                return {
                    type: 'tag_drilldown',
                    title: cName,
                    subtitle: `Tag Breakdown · ${currentDonutType.toUpperCase()}`,
                    total: amt,
                    isIncome: currentDonutType === 'income',
                    data: tagList
                };
            }
        });
    });

    document.getElementById('donut-center-label').innerText = currentDonutType.toUpperCase();
    document.getElementById('donut-center-total').innerText = totalValueDonut > 0 ? formatNumberShort(totalValueDonut) : '0';

    if (donutChartInstance) donutChartInstance.destroy();
    donutChartInstance = new Chart(document.getElementById('chart-donut'), {
        type: 'doughnut',
        data: {
            labels: donutLabels,
            datasets: [{
                data: donutData.length > 0 ? donutData : [1],
                backgroundColor: donutData.length > 0 ? donutBg : ['#374151'],
                borderWidth: 0,
                cutout: '75%'
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: donutData.length > 0,
                    callbacks: {
                        label: function(context) {
                            return ' ' + context.parsed.toLocaleString('en-US') + ' VND';
                        }
                    }
                }
            },
            onClick: (event, elements) => {
                // Click vào mảng -> Mở list
                if (elements.length > 0 && donutData.length > 0) {
                    const index = elements[0].index;
                    const cName = donutLabels[index];
                    // Tìm item
                    const item = categoryObjList.find(c => c.name === cName);
                    if (item) {
                        openDrilldownModal({
                            type: 'category_drilldown',
                            title: filterMode === 'month' ? `${cName} (${currentDate.toLocaleDateString('en-US', {month: 'short'})})` : `${cName} (${currentDate.getFullYear()})`,
                            subtitle: `Structure · ${currentDonutType.toUpperCase()}`,
                            total: totalValueDonut, 
                            isIncome: currentDonutType === 'income',
                            data: [item] // Chỉ đưa cái nó vừa click cho modal nếu muốn, hoặc đưa nguyên mảng để tiện nhìn
                        });
                        // Thay vì mở list 1 phần tử, mở mẹ list FULL nhưng focus or just full. Let's open full for context!
                        // Oh wait, the require is click segment -> show ALL categories, then drill to ALL tags.
                    }
                } else if (donutData.length > 0) {
                    // Click vùng rỗng giữa đồ thị -> Bật Full List
                    openDrilldownModal({
                        type: 'category_list_drilldown',
                        title: filterMode === 'month' ? currentDate.toLocaleDateString('en-US', {month: 'long', year: 'numeric'}) : currentDate.getFullYear().toString(),
                        subtitle: `Categories · ${currentDonutType.toUpperCase()}`,
                        total: totalValueDonut,
                        isIncome: currentDonutType === 'income',
                        data: categoryObjList
                    });
                }
            }
        }
    });

    // Optional: Add click on center to launch full list
    const chartContainer = document.getElementById('chart-donut').parentElement;
    chartContainer.onclick = (e) => {
        // Only if didn't hit canvas element
        if(donutData.length > 0) {
             openDrilldownModal({
                type: 'category_list_drilldown',
                title: filterMode === 'month' ? currentDate.toLocaleDateString('en-US', {month: 'long', year: 'numeric'}) : currentDate.getFullYear().toString(),
                subtitle: `Categories overview`,
                total: totalValueDonut,
                isIncome: currentDonutType === 'income',
                data: categoryObjList
            });
        }
    }


    // 3. HORIZONTAL BAR CHART (Days in Month OR Months in Year)
    const periodMap = {};
    let barLabels = [];
    if (filterMode === 'month') {
        const daysInMon = new Date(targetYear, targetMonth + 1, 0).getDate();
        for (let i = 1; i <= daysInMon; i++) {
            periodMap[i] = { in: 0, out: 0, dataTxns: [] };
            barLabels.push(i.toString());
        }
        currentPeriodTxns.forEach(t => {
            const d = new Date(t.transaction_date).getDate();
            const amt = Number(t.base_amount || t.amount);
            if (t.type === 'income') periodMap[d].in += amt;
            else if (t.type === 'expense') periodMap[d].out += amt;
            periodMap[d].dataTxns.push(t);
        });
    } else {
        for (let i = 0; i < 12; i++) {
            periodMap[i] = { in: 0, out: 0, dataTxns: [] };
        }
        barLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        currentPeriodTxns.forEach(t => {
            const m = new Date(t.transaction_date).getMonth();
            const amt = Number(t.base_amount || t.amount);
            if (t.type === 'income') periodMap[m].in += amt;
            else if (t.type === 'expense') periodMap[m].out += amt;
            periodMap[m].dataTxns.push(t);
        });
    }

    const inData = [];
    const outData = [];
    let heightMultiplier = barLabels.length;

    barLabels.forEach((lbl, i) => {
        const key = filterMode === 'month' ? i + 1 : i;
        inData.push(periodMap[key].in);
        outData.push(periodMap[key].out); // we can push positive or negative. Stacked horizontal needs matching axes. Wait array is vertical or horizontal?
    });

    // Determine container height
    const container = document.getElementById('chart-bar-container');
    container.style.height = `${heightMultiplier * 25 + 50}px`; // dynamic height for scroll

    if (barChartInstance) barChartInstance.destroy();
    document.getElementById('bar-chart-title').innerText = filterMode === 'month' ? 'Daily Net Flow' : 'Monthly Net Flow';
    
    barChartInstance = new Chart(document.getElementById('chart-bar'), {
        type: 'bar',
        data: {
            labels: barLabels,
            datasets: [
                {
                    label: 'Income',
                    data: inData,
                    backgroundColor: '#10B981',
                    borderRadius: 4,
                    barPercentage: 0.8
                },
                {
                    label: 'Expense',
                    data: outData.map(v => -v), // Negative for opposing bars!
                    backgroundColor: '#EF4444',
                    borderRadius: 4,
                    barPercentage: 0.8
                }
            ]
        },
        options: {
            indexAxis: 'y', // This makes it horizontal!
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            return ' ' + Math.abs(context.parsed.x).toLocaleString('en-US') + ' VND';
                        }
                    }
                }
            },
            scales: {
                x: { stacked: true, grid: { color: '#374151' }, ticks: { callback: function(val) { return (Math.abs(val)/1000).toFixed(0) + 'K'; }} },
                y: { stacked: true, grid: { display: false } }
            },
            onClick: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const key = filterMode === 'month' ? index + 1 : index;
                    const labelStr = barLabels[index];
                    
                    const pTxns = periodMap[key].dataTxns;
                    if (pTxns.length === 0) return;

                    // Compute categories for this specific day/month
                    const localCatData = {};
                    let localTotalIn = 0;
                    let localTotalOut = 0;
                    pTxns.forEach(t => {
                        if (t.type === 'income') localTotalIn += Number(t.base_amount || t.amount);
                        else localTotalOut += Number(t.base_amount || t.amount);
                    });

                    // Function to build categories & tags for a specific type (income/expense)
                    const getCatGetter = (typeFilter) => {
                        return () => {
                            const localCatTotals = {};
                            let localTotal = 0;
                            pTxns.filter(t => t.type === typeFilter).forEach(t => {
                                const cName = (t.categories && t.categories.name) ? t.categories.name : (t.category_name || 'Unknown');
                                const amt = Number(t.base_amount || t.amount);
                                if (!localCatTotals[cName]) {
                                    localCatTotals[cName] = { amount: 0, tagTotals: {} };
                                }
                                localCatTotals[cName].amount += amt;
                                localTotal += amt;
                                
                                const tagName = (t.tags && t.tags.name) ? t.tags.name : (t.tag_name || 'No Tag');
                                if (!localCatTotals[cName].tagTotals[tagName]) localCatTotals[cName].tagTotals[tagName] = 0;
                                localCatTotals[cName].tagTotals[tagName] += amt;
                            });
                            
                            const catList = [];
                            Object.keys(localCatTotals).sort((a,b) => localCatTotals[b].amount - localCatTotals[a].amount).forEach(cName => {
                                const cAmt = localCatTotals[cName].amount;
                                catList.push({
                                    name: cName,
                                    amount: cAmt,
                                    percent: localTotal > 0 ? ((cAmt / localTotal) * 100).toFixed(1) : 0,
                                    isIncome: typeFilter === 'income',
                                    nextStateGetter: () => {
                                        const tagList = [];
                                        const tTotals = localCatTotals[cName].tagTotals;
                                        Object.keys(tTotals).sort((a,b) => tTotals[b] - tTotals[a]).forEach(tName => {
                                            const tAmt = tTotals[tName];
                                            tagList.push({
                                                name: tName,
                                                amount: tAmt,
                                                percent: cAmt > 0 ? ((tAmt / cAmt) * 100).toFixed(1) : 0,
                                                isIncome: typeFilter === 'income'
                                            });
                                        });
                                        return {
                                            type: `time_tag_drilldown_${typeFilter}`,
                                            title: cName,
                                            subtitle: `Tag Breakdown · ${typeFilter.toUpperCase()}`,
                                            total: cAmt,
                                            isIncome: typeFilter === 'income',
                                            data: tagList
                                        };
                                    }
                                });
                            });
                            
                            return {
                                type: `time_cat_drilldown_${typeFilter}`,
                                title: typeFilter === 'income' ? 'Income' : 'Expense',
                                subtitle: `Categories · ${filterMode === 'month' ? labelStr : labelStr + ' ' + currentDate.getFullYear()}`,
                                total: localTotal,
                                isIncome: typeFilter === 'income',
                                data: catList
                            };
                        }
                    };

                    // Build modal
                    openDrilldownModal({
                        type: 'time_drilldown',
                        title: filterMode === 'month' ? `${labelStr} ${currentDate.toLocaleDateString('en-US', {month:'short'})}` : `${labelStr} ${currentDate.getFullYear()}`,
                        subtitle: `Timeflow Breakdown`,
                        total: localTotalIn - localTotalOut,
                        isIncome: (localTotalIn - localTotalOut) >= 0,
                        data: [
                            { name: 'Income', amount: localTotalIn, percent: 100, isIncome: true, nextStateGetter: getCatGetter('income') },
                            { name: 'Expense', amount: localTotalOut, percent: 100, isIncome: false, nextStateGetter: getCatGetter('expense') }
                        ]
                    });
                }
            }
        }
    });
}

function generateColorFromString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${Math.abs(hash) % 360}, 70%, 50%)`;
    return color;
}

function formatNumberShort(num) {
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
}
